import React, { useState } from "react";
import styled from "styled-components";
import { useAuth } from "../context/AuthContext";
import { useProducts } from "../hooks/useProducts";
import { db } from "../firebase";
import { collection, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from "firebase/firestore";
import { uploadImage } from "../cloudinary";
import { theme } from "../components/theme";
import logo from "../assets/logo.png";

const EMPTY_FORM = {
  name: "", shortDesc: "", description: "",
  price14: "", price21: "", badge: "", imageUrl: "",
};

export default function AdminDashboard() {
  const { logout } = useAuth();
  const { products, loading } = useProducts();

  const [form, setForm] = useState(EMPTY_FORM);
  const [editId, setEditId] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [view, setView] = useState("list"); // "list" | "form"
  const [msg, setMsg] = useState("");

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const openAdd = () => {
    setForm(EMPTY_FORM);
    setEditId(null);
    setImageFile(null);
    setImagePreview("");
    setView("form");
  };

  const openEdit = (p) => {
    setForm({
      name: p.name || "",
      shortDesc: p.shortDesc || "",
      description: p.description || "",
      price14: p.price14 || "",
      price21: p.price21 || "",
      badge: p.badge || "",
      imageUrl: p.imageUrl || "",
    });
    setEditId(p.id);
    setImageFile(null);
    setImagePreview(p.imageUrl || "");
    setView("form");
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name || !form.price14 || !form.price21) {
      setMsg("Emri dhe çmimet janë të detyrueshme.");
      return;
    }
    setSaving(true);
    setMsg("");
    try {
      let imageUrl = form.imageUrl;
      if (imageFile) {
        imageUrl = await uploadImage(imageFile);
      }

      const data = {
        name: form.name,
        shortDesc: form.shortDesc,
        description: form.description,
        price14: parseFloat(form.price14),
        price21: parseFloat(form.price21),
        badge: form.badge,
        imageUrl,
      };

      if (editId) {
        await updateDoc(doc(db, "products", editId), data);
        setMsg("Produkti u përditësua!");
      } else {
        await addDoc(collection(db, "products"), { ...data, createdAt: serverTimestamp() });
        setMsg("Produkti u shtua!");
      }

      setView("list");
      setForm(EMPTY_FORM);
      setEditId(null);
      setImageFile(null);
      setImagePreview("");
    } catch (err) {
      setMsg("Gabim: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteDoc(doc(db, "products", id));
      setDeleteConfirm(null);
      setMsg("Produkti u fshi.");
    } catch (err) {
      setMsg("Gabim gjatë fshirjes.");
    }
  };

  return (
    <Page>
      <Sidebar>
        <SidebarLogo src={logo} alt="OrgaLife" />
        <SidebarNav>
          <NavItem active={true}>📦 Produktet</NavItem>
        </SidebarNav>
        <LogoutBtn onClick={logout}>Dil ↗</LogoutBtn>
      </Sidebar>

      <Main>
        <TopBar>
          <PageTitle>
            {view === "list" ? "Produktet" : editId ? "Ndrysho Produktin" : "Shto Produkt të Ri"}
          </PageTitle>
          {view === "list" ? (
            <AddBtn onClick={openAdd}>+ Shto Produkt</AddBtn>
          ) : (
            <CancelBtn onClick={() => setView("list")}>← Kthehu</CancelBtn>
          )}
        </TopBar>

        {msg && <Msg>{msg}</Msg>}

        {view === "list" && (
          loading ? (
            <LoadingText>Duke ngarkuar...</LoadingText>
          ) : (
            <ProductTable>
              <thead>
                <tr>
                  <Th>Foto</Th>
                  <Th>Emri</Th>
                  <Th>14-shot €</Th>
                  <Th>21-shot €</Th>
                  <Th>Badge</Th>
                  <Th>Veprime</Th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id}>
                    <Td><TableImg src={p.imageUrl} alt={p.name} /></Td>
                    <Td><strong style={{ color: theme.colors.textPrimary }}>{p.name}</strong></Td>
                    <Td>€{p.price14?.toFixed(2)}</Td>
                    <Td>€{p.price21?.toFixed(2)}</Td>
                    <Td>{p.badge || "—"}</Td>
                    <Td>
                      <ActionRow>
                        <EditBtn onClick={() => openEdit(p)}>Ndrysho</EditBtn>
                        {deleteConfirm === p.id ? (
                          <>
                            <ConfirmBtn onClick={() => handleDelete(p.id)}>Po, fshi</ConfirmBtn>
                            <CancelSmBtn onClick={() => setDeleteConfirm(null)}>Anulo</CancelSmBtn>
                          </>
                        ) : (
                          <DeleteBtn onClick={() => setDeleteConfirm(p.id)}>Fshi</DeleteBtn>
                        )}
                      </ActionRow>
                    </Td>
                  </tr>
                ))}
                {products.length === 0 && (
                  <tr><Td colSpan={6} style={{ textAlign: "center", color: theme.colors.textMuted, padding: "2rem" }}>
                    Nuk ka produkte. Shtoni të parën!
                  </Td></tr>
                )}
              </tbody>
            </ProductTable>
          )
        )}

        {view === "form" && (
          <FormCard onSubmit={handleSave}>
            <FormGrid>
              <FormSection>
                <FieldGroup>
                  <Label>Emri i Produktit *</Label>
                  <Input value={form.name} onChange={set("name")} placeholder="p.sh. Detox Ginger Shot" required />
                </FieldGroup>
                <FieldGroup>
                  <Label>Përshkrim i shkurtër</Label>
                  <Input value={form.shortDesc} onChange={set("shortDesc")} placeholder="1-2 fjali për kartën e produktit" />
                </FieldGroup>
                <FieldGroup>
                  <Label>Përshkrim i plotë</Label>
                  <Textarea value={form.description} onChange={set("description")} rows={4} placeholder="Përshkrimi i plotë i produktit..." />
                </FieldGroup>
                <PriceRow>
                  <FieldGroup>
                    <Label>Çmimi 14 shots (€) *</Label>
                    <Input type="number" step="0.01" value={form.price14} onChange={set("price14")} placeholder="0.00" required />
                  </FieldGroup>
                  <FieldGroup>
                    <Label>Çmimi 21 shots (€) *</Label>
                    <Input type="number" step="0.01" value={form.price21} onChange={set("price21")} placeholder="0.00" required />
                  </FieldGroup>
                </PriceRow>
                <FieldGroup>
                  <Label>Badge (opsionale)</Label>
                  <Input value={form.badge} onChange={set("badge")} placeholder="p.sh. Bestseller, New, Sale..." />
                </FieldGroup>
              </FormSection>

              <FormSection>
                <Label>Fotoja e Produktit</Label>
                <ImageUploadArea>
                  {imagePreview ? (
                    <PreviewImg src={imagePreview} alt="Preview" />
                  ) : (
                    <UploadPlaceholder>
                      <span>📷</span>
                      <p>Kliko për të ngarkuar foto</p>
                    </UploadPlaceholder>
                  )}
                  <ImageInput type="file" accept="image/*" onChange={handleImageChange} />
                </ImageUploadArea>
                {imagePreview && (
                  <ChangeImageBtn type="button" onClick={() => document.querySelector('input[type=file]').click()}>
                    Ndrysho foton
                  </ChangeImageBtn>
                )}
                <Label style={{ marginTop: "1rem" }}>Ose URL e fotografisë (Cloudinary)</Label>
                <Input value={form.imageUrl} onChange={set("imageUrl")} placeholder="https://res.cloudinary.com/..." />
                <CloudinaryNote>
                  💡 Ngarko nga kompjuteri juaj (rekomandohet) ose vendosni URL-në e Cloudinary-t direkt.
                </CloudinaryNote>
              </FormSection>
            </FormGrid>

            <SaveBtn type="submit" disabled={saving}>
              {saving ? "Duke ruajtur..." : editId ? "Përditëso Produktin" : "Shto Produktin"}
            </SaveBtn>
          </FormCard>
        )}
      </Main>
    </Page>
  );
}

/* ── Styles ── */
const Page = styled.div`
  min-height: 100vh; background: ${theme.colors.bg};
  display: flex;
`;
const Sidebar = styled.aside`
  width: 220px; flex-shrink: 0;
  background: ${theme.colors.bgCard}; border-right: 1px solid ${theme.colors.borderSubtle};
  display: flex; flex-direction: column; padding: 2rem 1.5rem;
  position: sticky; top: 0; height: 100vh;
  @media (max-width: 768px) { display: none; }
`;
const SidebarLogo = styled.img`height: 36px; margin-bottom: 2rem; filter: brightness(0.8);`;
const SidebarNav = styled.nav`flex: 1;`;
const NavItem = styled.div`
  padding: 0.65rem 1rem; border-radius: ${theme.radius.md};
  font-size: 0.85rem; cursor: pointer; margin-bottom: 4px;
  background: ${({ active }) => active ? "rgba(137,209,70,0.1)" : "transparent"};
  color: ${({ active }) => active ? "#89d146" : theme.colors.textSecondary};
  border: 1px solid ${({ active }) => active ? "rgba(137,209,70,0.2)" : "transparent"};
  &:hover { background: rgba(137,209,70,0.08); }
`;
const LogoutBtn = styled.button`
  background: none; border: 1px solid ${theme.colors.borderSubtle};
  color: ${theme.colors.textMuted}; padding: 0.6rem 1rem; border-radius: 999px;
  cursor: pointer; font-size: 0.8rem; font-family: inherit;
  &:hover { border-color: #e05c5c; color: #e05c5c; }
`;
const Main = styled.main`flex: 1; padding: 2.5rem 3rem; overflow-y: auto;
  @media (max-width: 768px) { padding: 1.5rem; }
`;
const TopBar = styled.div`
  display: flex; justify-content: space-between; align-items: center; margin-bottom: 2rem;
`;
const PageTitle = styled.h1`
  font-family: 'Playfair Display', serif; font-size: 1.8rem; font-style: italic;
  color: ${theme.colors.textPrimary}; margin: 0;
`;
const AddBtn = styled.button`
  background: #89d146; color: white; border: none; padding: 0.7rem 1.5rem;
  border-radius: 999px; font-size: 0.85rem; font-weight: 600; cursor: pointer;
  font-family: inherit;
  &:hover { background: #7bc23e; }
`;
const CancelBtn = styled.button`
  background: none; border: 1px solid ${theme.colors.borderSubtle};
  color: ${theme.colors.textSecondary}; padding: 0.7rem 1.5rem; border-radius: 999px;
  font-size: 0.85rem; cursor: pointer; font-family: inherit;
  &:hover { border-color: #89d146; color: #89d146; }
`;
const Msg = styled.div`
  padding: 0.75rem 1rem; border-radius: ${theme.radius.md};
  background: rgba(137,209,70,0.1); border: 1px solid rgba(137,209,70,0.25);
  color: #89d146; font-size: 0.85rem; margin-bottom: 1.5rem;
`;
const LoadingText = styled.p`color: ${theme.colors.textMuted};`;
const ProductTable = styled.table`
  width: 100%; border-collapse: collapse;
  background: ${theme.colors.bgCard}; border-radius: ${theme.radius.lg};
  overflow: hidden; border: 1px solid ${theme.colors.borderSubtle};
`;
const Th = styled.th`
  text-align: left; padding: 1rem 1.25rem;
  font-family: ${theme.fonts.mono}; font-size: 0.65rem; letter-spacing: 0.15em;
  text-transform: uppercase; color: ${theme.colors.textMuted};
  border-bottom: 1px solid ${theme.colors.borderSubtle};
`;
const Td = styled.td`
  padding: 0.85rem 1.25rem; font-size: 0.85rem; color: ${theme.colors.textSecondary};
  border-bottom: 1px solid ${theme.colors.borderSubtle};
  tr:last-child & { border-bottom: none; }
`;
const TableImg = styled.img`
  width: 52px; height: 52px; object-fit: cover; border-radius: ${theme.radius.md};
`;
const ActionRow = styled.div`display: flex; gap: 6px; align-items: center;`;
const EditBtn = styled.button`
  background: none; border: 1px solid ${theme.colors.border};
  color: ${theme.colors.textSecondary}; padding: 4px 12px; border-radius: 999px;
  font-size: 0.75rem; cursor: pointer; font-family: inherit;
  &:hover { border-color: #89d146; color: #89d146; }
`;
const DeleteBtn = styled.button`
  background: none; border: 1px solid transparent;
  color: ${theme.colors.textMuted}; padding: 4px 12px; border-radius: 999px;
  font-size: 0.75rem; cursor: pointer; font-family: inherit;
  &:hover { border-color: #e05c5c; color: #e05c5c; }
`;
const ConfirmBtn = styled.button`
  background: #e05c5c; border: none; color: white;
  padding: 4px 12px; border-radius: 999px; font-size: 0.75rem; cursor: pointer;
`;
const CancelSmBtn = styled.button`
  background: none; border: 1px solid ${theme.colors.borderSubtle};
  color: ${theme.colors.textMuted}; padding: 4px 12px; border-radius: 999px;
  font-size: 0.75rem; cursor: pointer;
`;

const FormCard = styled.form`
  background: ${theme.colors.bgCard}; border: 1px solid ${theme.colors.borderSubtle};
  border-radius: ${theme.radius.lg}; padding: 2.5rem;
`;
const FormGrid = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 3rem;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;
const FormSection = styled.div`display: flex; flex-direction: column; gap: 1.25rem;`;
const FieldGroup = styled.div`display: flex; flex-direction: column; gap: 6px;`;
const PriceRow = styled.div`display: grid; grid-template-columns: 1fr 1fr; gap: 1rem;`;
const Label = styled.label`
  font-family: ${theme.fonts.mono}; font-size: 0.65rem; letter-spacing: 0.15em;
  text-transform: uppercase; color: ${theme.colors.textMuted};
`;
const Input = styled.input`
  background: ${theme.colors.bgSurface}; border: 1px solid ${theme.colors.borderSubtle};
  border-radius: ${theme.radius.md}; padding: 0.75rem 1rem;
  color: ${theme.colors.textPrimary}; font-size: 0.9rem; font-family: inherit;
  outline: none; width: 100%; box-sizing: border-box;
  &:focus { border-color: #89d146; }
  &::placeholder { color: ${theme.colors.textMuted}; }
`;
const Textarea = styled.textarea`
  background: ${theme.colors.bgSurface}; border: 1px solid ${theme.colors.borderSubtle};
  border-radius: ${theme.radius.md}; padding: 0.75rem 1rem;
  color: ${theme.colors.textPrimary}; font-size: 0.9rem; font-family: inherit;
  outline: none; resize: vertical; width: 100%; box-sizing: border-box;
  &:focus { border-color: #89d146; }
  &::placeholder { color: ${theme.colors.textMuted}; }
`;
const ImageUploadArea = styled.div`
  position: relative; width: 100%; aspect-ratio: 4/3; border-radius: ${theme.radius.md};
  border: 2px dashed ${theme.colors.borderSubtle}; overflow: hidden;
  cursor: pointer; transition: border-color 0.2s;
  &:hover { border-color: #89d146; }
`;
const PreviewImg = styled.img`width: 100%; height: 100%; object-fit: cover;`;
const UploadPlaceholder = styled.div`
  width: 100%; height: 100%; display: flex; flex-direction: column;
  align-items: center; justify-content: center; gap: 0.5rem;
  span { font-size: 2.5rem; }
  p { font-size: 0.82rem; color: ${theme.colors.textMuted}; margin: 0; }
`;
const ImageInput = styled.input`
  position: absolute; inset: 0; opacity: 0; cursor: pointer; width: 100%; height: 100%;
`;
const ChangeImageBtn = styled.button`
  background: none; border: 1px solid ${theme.colors.borderSubtle};
  color: ${theme.colors.textSecondary}; padding: 6px 14px; border-radius: 999px;
  font-size: 0.78rem; cursor: pointer; font-family: inherit; margin-top: 0.5rem;
  &:hover { border-color: #89d146; color: #89d146; }
`;
const CloudinaryNote = styled.p`
  font-size: 0.75rem; color: ${theme.colors.textMuted}; line-height: 1.6; margin: 0;
`;
const SaveBtn = styled.button`
  margin-top: 2rem; width: 100%; padding: 1rem; border-radius: 999px; border: none;
  background: #89d146; color: white; font-size: 1rem; font-weight: 600;
  font-family: inherit; cursor: pointer; transition: all 0.25s ease;
  &:hover:not(:disabled) { background: #7bc23e; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;
